import { worstStatus, WHOLE_SHIFT_LABEL, type CoverageReportRow, type CoverageReportStatus } from './coverageReport'

/**
 * The two-week manpower report (docs/product/reports.md): for each day of the
 * pay period the manpower on the schedule, how much of it is available, who is
 * out, and the planned overtime, with the same figures by shift. Pure, built
 * from the coverage report rows, so it follows the same filters and the
 * exports read the same result.
 */
// The share of manpower that should be available; a figure under it is marked.
export const AVAILABLE_THRESHOLD_PERCENT = 70

export const belowAvailableThreshold = (value: number | null) => value !== null && value < AVAILABLE_THRESHOLD_PERCENT

export type ManpowerRow = {
  key: string
  date: string
  shift: string | null
  totalManpower: number
  onDuty: number
  available: number
  // Available as a share of total manpower; null when no one is scheduled.
  availablePercent: number | null
  onLeave: number
  detailedOut: number
  plannedOvertime: number
  status: CoverageReportStatus
}

export type ManpowerTotals = Omit<ManpowerRow, 'key' | 'date' | 'shift' | 'status'>

function percent(part: number, whole: number): number | null {
  return whole === 0 ? null : Math.round((part / whole) * 100)
}

const shiftKey = (row: CoverageReportRow) => `${row.date}|${row.teamId}|${row.shiftId ?? ''}`

function sumRows(rows: CoverageReportRow[]): ManpowerTotals {
  // A shift-wide row counts each person once across the assignments beneath
  // it, so it stands for its shift; summing the assignment rows instead would
  // count a person with segments under two assignments twice. Where a filter
  // has removed the shift-wide row, the assignment rows that remain are summed.
  const totalled = new Set(rows.filter((row) => row.assignment === WHOLE_SHIFT_LABEL).map(shiftKey))
  const counted = rows.filter((row) => (row.assignment === WHOLE_SHIFT_LABEL ? true : !totalled.has(shiftKey(row))))
  const totalManpower = counted.reduce((sum, row) => sum + row.scheduled, 0)
  const available = counted.reduce((sum, row) => sum + row.available, 0)
  return {
    totalManpower,
    onDuty: counted.reduce((sum, row) => sum + row.onDuty, 0),
    available,
    availablePercent: percent(available, totalManpower),
    onLeave: counted.reduce((sum, row) => sum + row.onLeave, 0),
    detailedOut: counted.reduce((sum, row) => sum + row.detailedOut, 0),
    plannedOvertime: counted.reduce((sum, row) => sum + row.plannedOvertime, 0),
  }
}

export function manpowerByDay(rows: CoverageReportRow[]): ManpowerRow[] {
  const dates = Array.from(new Set(rows.map((row) => row.date))).sort()
  return dates.map((date) => {
    const day = rows.filter((row) => row.date === date)
    return { key: date, date, shift: null, ...sumRows(day), status: worstStatus(day.map((row) => row.status)) }
  })
}

export function manpowerByShift(rows: CoverageReportRow[]): ManpowerRow[] {
  const groups = new Map<string, CoverageReportRow[]>()
  for (const row of rows) {
    const key = `${row.date}|${row.shift}`
    groups.set(key, [...(groups.get(key) ?? []), row])
  }
  return Array.from(groups.entries())
    .map(([key, group]) => ({
      key,
      date: group[0].date,
      shift: group[0].shift,
      ...sumRows(group),
      status: worstStatus(group.map((row) => row.status)),
    }))
    .sort((a, b) => a.date.localeCompare(b.date) || (a.shift ?? '').localeCompare(b.shift ?? ''))
}

// Over the whole range, so the percentage is person-days available over
// person-days scheduled, not an average of the daily percentages.
export function manpowerTotals(rows: CoverageReportRow[]): ManpowerTotals {
  return sumRows(rows)
}
